/**
 * /tenants/$slug/directory/scim-tokens — route shim.
 *
 * Validates URL search params via Zod, prefetches the token
 * list via `ensureQueryData`, then mounts the SCIM tokens
 * management page.
 *
 * Permission gating happens inside the page component itself.
 * The backend's PermSCIMRead gate returns 403, mapped by the
 * API client to ForbiddenError, which the loader propagates.
 * That gives tenant admins without permission a clean error
 * boundary rather than a half-rendered table.
 */

import { createFileRoute } from "@tanstack/react-router";
import { fetchAllScimTokens } from "@/features/directory/queries";
import {
  scimTokensSearchSchema,
  type ScimTokensSearch,
} from "@/features/directory/scim-tokens-search";
import { ScimTokensPage } from "@/features/directory/pages/scim-tokens-page";

export const Route = createFileRoute(
  "/_authed/tenants/$slug/directory/scim-tokens",
)({
  validateSearch: (raw: Record<string, unknown>): ScimTokensSearch =>
    scimTokensSearchSchema.parse(raw),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["scim", "tokens-all", context.tenant.id],
      queryFn: () => fetchAllScimTokens(context.tenant.id),
    }),
  component: ScimTokensPage,
});
